import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  GraduationCap, 
  Globe, 
  Calendar,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  LocateFixed,
  Shield,
  Star,
  Users,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Country codes with flags
const countryCodes = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'USA / Canada', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'UK', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+64', country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: '+353', country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+43', country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: '+45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '+358', country: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: '+36', country: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: '+356', country: 'MT', name: 'Malta', flag: '🇲🇹' },
  { code: '+352', country: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
  { code: '+371', country: 'LV', name: 'Latvia', flag: '🇱🇻' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'NP', name: 'Nepal', flag: '🇳🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
];

// Map country code from geolocation
const getCountryCodeFromCountry = (countryCode) => {
  // Canada shares +1 with US — collapse to the US entry so the Select stays unique
  if (countryCode === 'CA') return '+1';
  const found = countryCodes.find(c => c.country === countryCode);
  return found ? found.code : '+91'; // Default to India
};

// Study options
const studyDestinations = [
  'Australia', 'Austria', 'Canada', 'Denmark', 'Dubai', 'Finland', 'France', 
  'Germany', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Luxembourg', 'Malaysia', 
  'Malta', 'Netherlands', 'New Zealand', 'Singapore', 'Spain', 'Sweden', 
  'Switzerland', 'UK', 'USA', 'Not Decided', 'Others'
];

const studyAreas = [
  'Accounting', 'Agricultural Sciences', 'Arts & Design', 'Building & Architecture',
  'Business', 'Computing & IT', 'Economics', 'Engineering', 'Environmental Science',
  'Health & Medicine', 'Hospitality & Tourism', 'Human Welfare Studies & Services',
  'Language & Culture', 'Law & Legal Studies', 'Marketing & Media Communications',
  'Performing Arts & Music', 'Political & Social Science', 'Teaching & Education',
  'Pharmacy', 'Sciences', 'Not Decided', 'Others'
];

const studyIntakes = [
  { value: 'q1', label: 'Jan - Mar' },
  { value: 'q2', label: 'Apr - Jun' },
  { value: 'q3', label: 'Jul - Sep' },
  { value: 'q4', label: 'Oct - Dec' },
  { value: 'not_decided', label: 'Flexible' }
];

const currentYear = new Date().getFullYear();
const studyYears = [
  { value: `${currentYear}`, label: `${currentYear}` },
  { value: `${currentYear + 1}`, label: `${currentYear + 1}` },
  { value: `${currentYear + 2}`, label: `${currentYear + 2}` },
  { value: `${currentYear + 3}`, label: `${currentYear + 3}` },
  { value: 'not_sure', label: 'Flexible' }
];

// Branch offices for dropdown
const branchOffices = [
  { id: 'ahmedabad', city: 'Ahmedabad' },
  { id: 'bengaluru', city: 'Bengaluru' },
  { id: 'chennai', city: 'Chennai' },
  { id: 'gurgaon', city: 'Gurgaon' },
  { id: 'hyderabad-somajiguda', city: 'Hyderabad', area: 'Somajiguda' },
  { id: 'hyderabad-kukatpally', city: 'Hyderabad', area: 'Kukatpally' },
  { id: 'kottayam', city: 'Kottayam' },
  { id: 'kochi', city: 'Kochi' },
  { id: 'pune', city: 'Pune' },
  { id: 'vijayawada', city: 'Vijayawada' },
  { id: 'vizag', city: 'Visakhapatnam' },
  { id: 'c2x-new', city: 'Other Location', area: 'Virtual Consultation' }
];

export const LeadCaptureForm = ({ 
  onSuccess, 
  className = '',
  prefilledData = {},
  showHeader = true,
  variant = 'default' // 'default' | 'modal' | 'compact'
}) => {
  const { isDark } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: prefilledData.firstName || '',
    lastName: prefilledData.lastName || '',
    email: prefilledData.email || '',
    countryCode: prefilledData.countryCode || '+91',
    phone: prefilledData.phone || '',
    studyDestination: prefilledData.studyDestination || '',
    studyArea: prefilledData.studyArea || '',
    studyIntake: prefilledData.studyIntake || '',
    studyYear: prefilledData.studyYear || '',
    preferredBranch: prefilledData.preferredBranch || '',
    detectedCity: prefilledData.detectedCity || '',
    detectedCountry: prefilledData.detectedCountry || '',
    detectedState: prefilledData.detectedState || '',
    agreeTerms: false,
    agreeContact: false
  });

  const totalSteps = 3;
  const isModal = variant === 'modal';
  const isCompact = variant === 'compact';

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    // Check for common typos
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain) {
      // Check for obvious typos like gmial, gmil, yahooo
      const typoPatterns = {
        'gmial.com': 'gmail.com',
        'gmil.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'gamil.com': 'gmail.com',
        'gnail.com': 'gmail.com',
        'yaho.com': 'yahoo.com',
        'yahooo.com': 'yahoo.com',
        'hotmal.com': 'hotmail.com',
        'outllok.com': 'outlook.com',
        'outlok.com': 'outlook.com'
      };
      if (typoPatterns[domain]) {
        return `Did you mean ${email.split('@')[0]}@${typoPatterns[domain]}?`;
      }
    }
    return null;
  };

  // Phone validation
  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    if (phone.length < 10) return 'Please enter a valid 10-digit phone number';
    return null;
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({...formData, email});
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors({...errors, email: null});
    }
  };

  // Validate email on blur
  const handleEmailBlur = () => {
    const error = validateEmail(formData.email);
    if (error) {
      setErrors({...errors, email: error});
    }
  };

  // Geolocation mapping function
  const mapLocationToBranch = (city, state) => {
    const cityLower = city?.toLowerCase()?.trim() || '';
    const stateLower = state?.toLowerCase()?.trim() || '';
    
    const cityMappings = {
      // Gujarat
      'ahmedabad': 'ahmedabad',
      
      // Karnataka
      'bengaluru': 'bengaluru', 'bangalore': 'bengaluru',
      
      // Tamil Nadu
      'chennai': 'chennai',
      
      // Delhi & Haryana only → Gurgaon
      'gurgaon': 'gurgaon', 'gurugram': 'gurgaon',
      'delhi': 'gurgaon', 'new delhi': 'gurgaon',
      'faridabad': 'gurgaon',
      'rohtak': 'gurgaon', 'panipat': 'gurgaon', 'sonipat': 'gurgaon',
      'karnal': 'gurgaon', 'ambala': 'gurgaon', 'hisar': 'gurgaon',
      'bhiwani': 'gurgaon', 'jhajjar': 'gurgaon', 'rewari': 'gurgaon',
      'palwal': 'gurgaon',
      
      // Telangana - Hyderabad city areas
      'hyderabad': 'hyderabad-somajiguda',
      'secunderabad': 'hyderabad-somajiguda',
      'somajiguda': 'hyderabad-somajiguda',
      'begumpet': 'hyderabad-somajiguda',
      'ameerpet': 'hyderabad-somajiguda',
      'banjara hills': 'hyderabad-somajiguda',
      'jubilee hills': 'hyderabad-somajiguda',
      'himayatnagar': 'hyderabad-somajiguda',
      'abids': 'hyderabad-somajiguda',
      'nampally': 'hyderabad-somajiguda',
      'charminar': 'hyderabad-somajiguda',
      'dilsukhnagar': 'hyderabad-somajiguda',
      'lb nagar': 'hyderabad-somajiguda',
      'uppal': 'hyderabad-somajiguda',
      // Hyderabad West/IT Corridor → Kukatpally
      'kukatpally': 'hyderabad-kukatpally', 
      'miyapur': 'hyderabad-kukatpally',
      'kondapur': 'hyderabad-kukatpally', 
      'gachibowli': 'hyderabad-kukatpally',
      'madhapur': 'hyderabad-kukatpally',
      'hitec city': 'hyderabad-kukatpally',
      'kphb': 'hyderabad-kukatpally',
      'jntu': 'hyderabad-kukatpally',
      'lingampally': 'hyderabad-kukatpally',
      'chandanagar': 'hyderabad-kukatpally',
      'patancheru': 'hyderabad-kukatpally',
      // Telangana outside Hyderabad → Somajiguda (main office)
      'warangal': 'hyderabad-somajiguda',
      'nizamabad': 'hyderabad-somajiguda',
      'karimnagar': 'hyderabad-somajiguda',
      'khammam': 'hyderabad-somajiguda',
      'mahbubnagar': 'hyderabad-somajiguda',
      'nalgonda': 'hyderabad-somajiguda',
      'adilabad': 'hyderabad-somajiguda',
      'medak': 'hyderabad-somajiguda',
      'sangareddy': 'hyderabad-somajiguda',
      'siddipet': 'hyderabad-somajiguda',
      'suryapet': 'hyderabad-somajiguda',
      'miryalaguda': 'hyderabad-somajiguda',
      'mancherial': 'hyderabad-somajiguda',
      'ramagundam': 'hyderabad-somajiguda',
      
      // Kerala - North → Kochi
      'kochi': 'kochi', 'ernakulam': 'kochi', 'cochin': 'kochi',
      'thrissur': 'kochi', 'palakkad': 'kochi',
      'kozhikode': 'kochi', 'calicut': 'kochi',
      'kannur': 'kochi', 'malappuram': 'kochi',
      // Kerala - South → Kottayam
      'kottayam': 'kottayam',
      'trivandrum': 'kottayam', 'thiruvananthapuram': 'kottayam',
      'kollam': 'kottayam', 'alappuzha': 'kottayam',
      'pathanamthitta': 'kottayam', 'idukki': 'kottayam',
      
      // Maharashtra
      'pune': 'pune', 'mumbai': 'pune', 'navi mumbai': 'pune',
      'thane': 'pune', 'nashik': 'pune', 'nagpur': 'pune',
      
      // Andhra Pradesh - South/Central → Vijayawada
      'vijayawada': 'vijayawada', 'guntur': 'vijayawada',
      'nellore': 'vijayawada', 'tirupati': 'vijayawada',
      'kurnool': 'vijayawada', 'anantapur': 'vijayawada',
      'kadapa': 'vijayawada', 'ongole': 'vijayawada',
      // Andhra Pradesh - North → Vizag
      'visakhapatnam': 'vizag', 'vizag': 'vizag',
      'vizianagaram': 'vizag', 'srikakulam': 'vizag',
      'rajahmundry': 'vizag', 'kakinada': 'vizag',
      'bhimavaram': 'vizag', 'eluru': 'vizag'
    };
    
    if (cityMappings[cityLower]) return cityMappings[cityLower];
    
    // State-level fallback mappings
    const stateMappings = {
      'gujarat': 'ahmedabad',
      'karnataka': 'bengaluru',
      'tamil nadu': 'chennai',
      // Delhi NCR → Gurgaon (only Haryana & Delhi)
      'haryana': 'gurgaon',
      'delhi': 'gurgaon',
      'new delhi': 'gurgaon',
      // Telangana → Somajiguda (main office)
      'telangana': 'hyderabad-somajiguda',
      // Kerala → Kochi (larger city)
      'kerala': 'kochi',
      // Maharashtra → Pune
      'maharashtra': 'pune',
      // Andhra Pradesh → Vijayawada (capital region)
      'andhra pradesh': 'vijayawada',
      // Nearby states with offices
      'odisha': 'vizag',
      'west bengal': 'vizag',
      'chhattisgarh': 'hyderabad-somajiguda',
      'madhya pradesh': 'pune',
      'goa': 'pune',
      // States routed to C2X New (Virtual Consultation)
      'uttar pradesh': 'c2x-new',
      'punjab': 'c2x-new',
      'rajasthan': 'c2x-new',
      'himachal pradesh': 'c2x-new',
      'uttarakhand': 'c2x-new',
      'jammu and kashmir': 'c2x-new',
      'chandigarh': 'c2x-new',
      'bihar': 'c2x-new',
      'jharkhand': 'c2x-new',
      'assam': 'c2x-new',
      'northeast': 'c2x-new'
    };
    
    return stateMappings[stateLower] || 'c2x-new'; // Unmapped locations → C2X New office in Zoho
  };

  // Auto-detect location on mount
  useEffect(() => {
    if (!formData.preferredBranch && navigator.geolocation) {
      setIsDetectingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );
            if (res.ok) {
              const data = await res.json();
              const city = data.address?.city || data.address?.town || '';
              const state = data.address?.state || '';
              const countryCode = data.address?.country_code?.toUpperCase() || 'IN';
              const branch = mapLocationToBranch(city, state);
              const phoneCode = getCountryCodeFromCountry(countryCode);
              setFormData(prev => ({ 
                ...prev, 
                preferredBranch: branch, 
                detectedCity: city,
                detectedCountry: countryCode,
                countryCode: phoneCode
              }));
            }
          } catch (e) { console.error(e); }
          setIsDetectingLocation(false);
        },
        () => setIsDetectingLocation(false),
        { enableHighAccuracy: false, timeout: 5000 }
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms || !formData.agreeContact) {
      toast.error('Please accept the terms and consent to proceed');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Clean and combine phone number for submission
      let cleanPhone = formData.phone.replace(/\s+/g, '').replace(/-/g, '');
      // Remove any existing country code from the phone if present
      if (cleanPhone.startsWith('+')) {
        cleanPhone = cleanPhone.replace(/^\+\d{1,3}/, '');
      }
      
      const submitData = {
        ...formData,
        phone: `${formData.countryCode} ${cleanPhone}`.trim()
      };
      
      const res = await fetch(`${API_URL}/api/public/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      if (res.ok) {
        const responseData = await res.json();
        toast.success('Thank you! Our counselor will contact you within 24 hours.');
        onSuccess?.(responseData); // Pass response data to callback
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  const nextStep = () => {
    if (currentStep === 1) {
      // Validate all Step 1 fields
      const newErrors = {};
      
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
      }
      
      const phoneError = validatePhone(formData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error('Please correct the errors before continuing');
        return;
      }
    }
    
    if (currentStep === 2 && !formData.studyDestination) {
      toast.error('Please select your study destination');
      return;
    }
    
    setErrors({});
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
              step < currentStep 
                ? 'bg-ux-green text-white' 
                : step === currentStep 
                  ? 'bg-ux-cyan text-white ring-4 ring-ux-cyan/20' 
                  : isDark ? 'bg-white/10 text-white/50' : 'bg-slate-100 text-slate-400'
            }`}
          >
            {step < currentStep ? <CheckCircle2 className="w-4 h-4" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-12 h-1 mx-1 rounded-full transition-all ${
              step < currentStep 
                ? 'bg-ux-green' 
                : isDark ? 'bg-white/10' : 'bg-slate-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  // Trust badges
  const TrustBadges = () => (
    <div className={`flex items-center justify-center gap-6 py-4 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
      <div className="flex items-center gap-1.5 text-xs">
        <Shield className="w-4 h-4 text-ux-green" />
        <span className={isDark ? 'text-white/60' : 'text-slate-500'}>100% Secure</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <Star className="w-4 h-4 text-ux-amber" />
        <span className={isDark ? 'text-white/60' : 'text-slate-500'}>4.9/5 Rating</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <Users className="w-4 h-4 text-ux-cyan" />
        <span className={isDark ? 'text-white/60' : 'text-slate-500'}>30K+ Students</span>
      </div>
    </div>
  );

  return (
    <div className={`${className} ${isDark ? 'bg-[#1e3340]' : 'bg-white'} rounded-3xl shadow-2xl overflow-hidden`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-ux-cyan to-ux-blue p-6 text-white text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="w-5 h-5" />
          <span className="text-sm font-medium opacity-90">Free Consultation</span>
        </div>
        <h3 className="text-2xl font-bold mb-1">Start Your Journey Today</h3>
        <p className="text-sm opacity-80">Get expert guidance in 24 hours</p>
      </div>

      <div className="p-6">
        <StepIndicator />
        
        <form onSubmit={handleSubmit}>
          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-4 animate-fadeIn">
              <div className="text-center mb-4">
                <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Let's get to know you
                </h4>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  Takes only 30 seconds
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>First Name *</Label>
                  <div className="relative">
                    <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                    <Input
                      value={formData.firstName}
                      onChange={(e) => {
                        setFormData({...formData, firstName: e.target.value});
                        if (errors.firstName) setErrors({...errors, firstName: null});
                      }}
                      className={`pl-9 ${errors.firstName ? 'border-red-500 focus:ring-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                      placeholder="John"
                      required
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Last Name</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className={isDark ? 'bg-white/10 border-white/20 text-white' : ''}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Email Address *</Label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${errors.email ? 'text-red-400' : isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    className={`pl-9 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                    placeholder="john@email.com"
                    required
                  />
                  {formData.email && !errors.email && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-green" />
                  )}
                </div>
                {errors.email && (
                  <p className={`text-xs mt-1 ${errors.email.includes('Did you mean') ? 'text-amber-500' : 'text-red-500'}`}>
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Phone Number *</Label>
                <div className="flex gap-2">
                  <Select 
                    value={formData.countryCode} 
                    onValueChange={(v) => setFormData({...formData, countryCode: v})}
                  >
                    <SelectTrigger className={`w-[100px] ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                      <SelectValue>
                        {(() => {
                          const selected = countryCodes.find(c => c.code === formData.countryCode);
                          return selected ? `${selected.flag} ${selected.code}` : '+91';
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {countryCodes.map((c) => (
                        <SelectItem key={`${c.country}-${c.code}`} value={c.code}>
                          <span className="flex items-center gap-2">
                            <span>{c.flag}</span>
                            <span>{c.code}</span>
                            <span className="text-xs text-slate-400">{c.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setFormData({...formData, phone: value});
                        if (errors.phone) setErrors({...errors, phone: null});
                      }}
                      className={`${errors.phone ? 'border-red-500 focus:ring-red-500' : ''} ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}
                      placeholder="98765 43210"
                      maxLength={10}
                      required
                    />
                    {formData.phone.length === 10 && (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-green" />
                    )}
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              <Button 
                type="button" 
                onClick={nextStep}
                className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white py-3"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Study Preferences */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-fadeIn">
              <div className="text-center mb-4">
                <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Your Study Goals
                </h4>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  Help us find the perfect fit
                </p>
              </div>

              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Where do you want to study? *</Label>
                <div className="relative">
                  <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <Select value={formData.studyDestination} onValueChange={(v) => setFormData({...formData, studyDestination: v})}>
                    <SelectTrigger className={`pl-9 ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {studyDestinations.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>What do you want to study?</Label>
                <div className="relative">
                  <GraduationCap className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <Select value={formData.studyArea} onValueChange={(v) => setFormData({...formData, studyArea: v})}>
                    <SelectTrigger className={`pl-9 ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      {studyAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Intake</Label>
                  <Select value={formData.studyIntake} onValueChange={(v) => setFormData({...formData, studyIntake: v})}>
                    <SelectTrigger className={isDark ? 'bg-white/10 border-white/20 text-white' : ''}>
                      <SelectValue placeholder="When?" />
                    </SelectTrigger>
                    <SelectContent>
                      {studyIntakes.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Year</Label>
                  <Select value={formData.studyYear} onValueChange={(v) => setFormData({...formData, studyYear: v})}>
                    <SelectTrigger className={isDark ? 'bg-white/10 border-white/20 text-white' : ''}>
                      <SelectValue placeholder="Year?" />
                    </SelectTrigger>
                    <SelectContent>
                      {studyYears.map(y => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={prevStep} className={`flex-1 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button type="button" onClick={nextStep} className="flex-1 bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Location & Consent */}
          {currentStep === 3 && (
            <div className="space-y-4 animate-fadeIn">
              <div className="text-center mb-4">
                <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Almost Done!
                </h4>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  Choose your nearest office
                </p>
              </div>

              <div>
                <Label className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Nearest Office</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                    <Select value={formData.preferredBranch} onValueChange={(v) => setFormData({...formData, preferredBranch: v})}>
                      <SelectTrigger className={`pl-9 ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                        <SelectValue placeholder={isDetectingLocation ? "Detecting..." : "Select office"} />
                      </SelectTrigger>
                      <SelectContent>
                        {branchOffices.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.city}{b.area ? ` (${b.area})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isDetectingLocation}
                    className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    onClick={() => {
                      setIsDetectingLocation(true);
                      navigator.geolocation?.getCurrentPosition(
                        async (pos) => {
                          try {
                            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&addressdetails=1`);
                            if (res.ok) {
                              const data = await res.json();
                              const city = data.address?.city || data.address?.town || '';
                              const state = data.address?.state || '';
                              setFormData(prev => ({ ...prev, preferredBranch: mapLocationToBranch(city, state), detectedCity: city }));
                            }
                          } catch(e) {}
                          setIsDetectingLocation(false);
                        },
                        () => setIsDetectingLocation(false)
                      );
                    }}
                  >
                    {isDetectingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
                  </Button>
                </div>
                {formData.detectedCity && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                    Detected: {formData.detectedCity}
                  </p>
                )}
              </div>

              {/* Consent checkboxes with better styling */}
              <div className={`space-y-3 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.agreeTerms}
                    onChange={(e) => setFormData({...formData, agreeTerms: e.target.checked})}
                    className="mt-0.5 w-5 h-5 rounded border-2 border-slate-300 text-ux-cyan focus:ring-ux-cyan cursor-pointer"
                  />
                  <span className={`text-sm leading-tight ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                    I agree to the <Link to="/terms" className="text-ux-cyan hover:underline font-medium">Terms & Conditions</Link>
                  </span>
                </label>
                
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.agreeContact}
                    onChange={(e) => setFormData({...formData, agreeContact: e.target.checked})}
                    className="mt-0.5 w-5 h-5 rounded border-2 border-slate-300 text-ux-cyan focus:ring-ux-cyan cursor-pointer"
                  />
                  <span className={`text-sm leading-tight ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                    I consent to being contacted via WhatsApp, Email & Phone
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={prevStep} className={`flex-1 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting || !formData.agreeTerms || !formData.agreeContact}
                  className="flex-1 bg-gradient-to-r from-ux-green to-emerald-500 hover:opacity-90 text-white font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Get Free Consultation
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>

        <TrustBadges />
      </div>
    </div>
  );
};

export default LeadCaptureForm;
